// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  FileText, 
  Search, 
  Download, 
  Send, 
  Calendar,
  DollarSign,
  Building2,
  RefreshCw,
  Play,
  CheckCircle,
  XCircle,
  Clock
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [billingRuns, setBillingRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [runningBilling, setRunningBilling] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      const token = localStorage.getItem("360wallet_token");
      if (!token) {
        toast.error("Please login again");
        return;
      }
      let url = `${API_URL}/api/billing/invoices`;
      const params = new URLSearchParams();
      if (filterMonth && filterMonth !== "all") params.append("billing_month", filterMonth);
      if (filterStatus && filterStatus !== "all") params.append("status", filterStatus);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load invoices");
      }
    }
  }, [filterMonth, filterStatus]);

  const fetchBillingRuns = useCallback(async () => {
    try {
      const token = localStorage.getItem("360wallet_token");
      const response = await axios.get(`${API_URL}/api/billing/runs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingRuns(response.data);
    } catch (error) {
      console.error("Failed to load billing runs");
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchInvoices(), fetchBillingRuns()]);
      setLoading(false);
    };
    loadData();
  }, [fetchInvoices, fetchBillingRuns]);

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem("360wallet_token");
      const response = await axios.get(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  const handleResendInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem("360wallet_token");
      await axios.post(`${API_URL}/api/billing/invoices/${invoiceId}/resend`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice email resent successfully");
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invoice");
    }
  };

  const handleRunBilling = async () => {
    setRunningBilling(true);
    try {
      const token = localStorage.getItem("360wallet_token");
      const response = await axios.post(`${API_URL}/api/billing/run-manual`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Billing run completed: ${response.data.total_invoices} invoices generated`);
      await Promise.all([fetchInvoices(), fetchBillingRuns()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run billing");
    } finally {
      setRunningBilling(false);
    }
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.counterparty_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalTransactions = filteredInvoices.reduce((sum, inv) => sum + inv.total_transactions, 0);

  const formatCurrency = (amount) => `R${amount.toFixed(2)}`;

  const StatusBadge = ({ status }) => {
    const styles = {
      sent: "bg-green-500/20 text-green-400",
      generated: "bg-yellow-500/20 text-yellow-400",
      failed: "bg-red-500/20 text-red-400"
    };
    const icons = {
      sent: <CheckCircle className="h-3 w-3" />,
      generated: <Clock className="h-3 w-3" />,
      failed: <XCircle className="h-3 w-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${styles[status] || styles.generated}`}>
        {icons[status]}
        {status.toUpperCase()}
      </span>
    );
  };

  const TierBadge = ({ tier }) => {
    const styles = {
      tier_1: "bg-blue-500/20 text-blue-400",
      tier_2: "bg-blue-500/20 text-blue-400",
      tier_3: "bg-purple-500/20 text-purple-400"
    };
    const labels = {
      tier_1: "Tier 1",
      tier_2: "Tier 2",
      tier_3: "Tier 3"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs ${styles[tier] || styles.tier_1}`}>
        {labels[tier] || tier}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">Invoices & Billing</h1>
          <p className="text-muted-foreground">Automated monthly billing and invoicing</p>
        </div>
        <Button 
          onClick={handleRunBilling} 
          disabled={runningBilling}
          data-testid="run-billing-btn"
        >
          {runningBilling ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Run Billing
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoices</p>
                <p className="text-xl font-mono font-bold">{filteredInvoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-xl font-mono font-bold">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Transactions Billed</p>
                <p className="text-xl font-mono font-bold">{totalTransactions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Calendar className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Billing Runs</p>
                <p className="text-xl font-mono font-bold">{billingRuns.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by counterparty or invoice number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="invoice-search"
              />
            </div>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="w-[180px]" data-testid="filter-month">
                <SelectValue placeholder="All Months" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                <SelectItem value="2026-01">January 2026</SelectItem>
                <SelectItem value="2025-12">December 2025</SelectItem>
                <SelectItem value="2025-11">November 2025</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="generated">Generated</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Counterparty</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="animate-pulse text-muted-foreground">Loading invoices...</div>
                  </TableCell>
                </TableRow>
              ) : filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">No invoices found</p>
                      <p className="text-xs text-muted-foreground">Run billing to generate invoices</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <code className="font-mono text-sm bg-secondary px-2 py-1 rounded">
                        {invoice.invoice_number}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="font-medium">{invoice.counterparty_name}</span>
                        <p className="text-xs text-muted-foreground">{invoice.counterparty_type}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TierBadge tier={invoice.tier} />
                    </TableCell>
                    <TableCell>{invoice.billing_period}</TableCell>
                    <TableCell className="font-mono">{invoice.total_transactions}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(invoice.rate_per_transaction)}</TableCell>
                    <TableCell className="font-mono font-medium">{formatCurrency(invoice.total_amount)}</TableCell>
                    <TableCell>
                      <StatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {invoice.pdf_stored && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                            title="Download PDF"
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResendInvoice(invoice.id)}
                          title="Resend Email"
                          data-testid={`resend-invoice-${invoice.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Billing Runs History */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Billing Run History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Billing Month</TableHead>
                <TableHead>Run Date</TableHead>
                <TableHead>Invoices</TableHead>
                <TableHead>Total Amount</TableHead>
                <TableHead>Emails Sent</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4 text-muted-foreground">
                    No billing runs yet
                  </TableCell>
                </TableRow>
              ) : (
                billingRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="font-medium">{run.billing_month}</TableCell>
                    <TableCell>{new Date(run.run_date).toLocaleString()}</TableCell>
                    <TableCell className="font-mono">{run.total_invoices}</TableCell>
                    <TableCell className="font-mono">{formatCurrency(run.total_amount)}</TableCell>
                    <TableCell>
                      <span className="text-green-400">{run.emails_sent}</span>
                      {run.emails_failed > 0 && (
                        <span className="text-red-400 ml-2">({run.emails_failed} failed)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={run.status === "completed" ? "sent" : run.status} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Invoices;
